import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Autocomplete,
  Paper,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { Search, Clear, LocationOn } from '@mui/icons-material';
import { Caretaker, SearchFilters } from '../services/caretakerSearchService';

interface CaretakerSearchFormProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  onSearch: () => void;
  loading?: boolean;
  getCaretakerSuggestions: (query: string) => Promise<Caretaker[]>;
  getSpecialtySuggestions: (query: string) => Promise<string[]>;
}

const CaretakerSearchForm: React.FC<CaretakerSearchFormProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  loading = false,
  getCaretakerSuggestions,
  getSpecialtySuggestions,
}) => {
  const [searchQuery, setSearchQuery] = useState(filters.query || '');
  const [zipInput, setZipInput] = useState(filters.zip || '');
  const [zipError, setZipError] = useState('');
  const [caretakerSuggestions, setCaretakerSuggestions] = useState<Caretaker[]>([]);
  const [specialtySuggestions, setSpecialtySuggestions] = useState<string[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Update search query when filters change externally
  useEffect(() => {
    setSearchQuery(filters.query || '');
  }, [filters.query]);

  useEffect(() => {
    setZipInput(filters.zip || '');
  }, [filters.zip]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  // Debounce the search query updates
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery !== filters.query) {
        onFiltersChange({
          ...filters,
          query: searchQuery,
        });
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, filters, onFiltersChange]);

  const handleZipChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 5);
    setZipInput(digits);
    if (digits.length > 0 && digits.length < 5) {
      setZipError('Enter a 5-digit ZIP code');
    } else {
      setZipError('');
    }
  };

  // Debounce the zip filter update — only send when fully valid or cleared
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const newZip = zipInput.length === 5 ? zipInput : '';
      if (newZip !== (filters.zip ?? '')) {
        onFiltersChange({ ...filters, zip: newZip || undefined });
      }
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [zipInput, filters, onFiltersChange]);

  const handleCaretakerSearch = async (query: string) => {
    if (query.length < 2) {
      setCaretakerSuggestions([]);
      return;
    }

    setSuggestionsLoading(true);
    try {
      const suggestions = await getCaretakerSuggestions(query);
      setCaretakerSuggestions(suggestions);
    } catch (error) {
      console.error('Error fetching caretaker suggestions:', error);
      setCaretakerSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleSpecialtySearch = async (query: string) => {
    if (query.length < 2) {
      setSpecialtySuggestions([]);
      return;
    }

    try {
      const suggestions = await getSpecialtySuggestions(query);
      setSpecialtySuggestions(suggestions);
    } catch (error) {
      console.error('Error fetching specialty suggestions:', error);
      setSpecialtySuggestions([]);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    onFiltersChange({
      ...filters,
      query: '',
    });
  };

  const clearZip = () => {
    setZipInput('');
    setZipError('');
    onFiltersChange({ ...filters, zip: undefined });
  };

  const getCaretakerDisplayName = (caretaker: Caretaker): string => {
    const firstName = caretaker.firstName || '';
    const lastName = caretaker.lastName || '';
    const name = `${firstName} ${lastName}`.trim();
    const specialty = caretaker.specialties.length > 0 ? caretaker.specialties[0].name : '';
    return specialty ? `${name} - ${specialty}` : name;
  };

  const getCaretakerSubtitle = (caretaker: Caretaker): string => {
    const specialties = caretaker.specialties.map(s => s.name).join(', ');
    const rating = caretaker.averageRating > 0 
      ? `${caretaker.averageRating} stars (${caretaker.reviewCount} reviews)`
      : 'No reviews yet';
    return `${specialties} • ${rating}`;
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom>
          Find Your Perfect Caretaker
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Search by specialty or caretaker name to find the right healthcare provider for you.
        </Typography>

        <Box display="flex" gap={2} alignItems="flex-start">
          {/* Search Input */}
          <Box flex={1}>
            <Autocomplete
              freeSolo
              options={[...caretakerSuggestions, ...specialtySuggestions]}
              value={searchQuery}
              onInputChange={(event, newValue) => {
                handleSearchChange(newValue || '');
                if (newValue) {
                  handleCaretakerSearch(newValue);
                  handleSpecialtySearch(newValue);
                }
              }}
              loading={suggestionsLoading}
              loadingText="Searching..."
              getOptionLabel={(option) => {
                if (typeof option === 'string') {
                  return option;
                }
                return getCaretakerDisplayName(option);
              }}
              renderOption={(props, option) => {
                if (typeof option === 'string') {
                  // Specialty suggestion
                  return (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2">
                          <strong>Specialty:</strong> {option}
                        </Typography>
                      </Box>
                    </Box>
                  );
                } else {
                  // Caretaker suggestion
                  return (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {getCaretakerDisplayName(option)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getCaretakerSubtitle(option)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search by specialty or caretaker name..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {suggestionsLoading && <CircularProgress color="inherit" size={20} />}
                        {searchQuery && (
                          <InputAdornment position="end">
                            <Button
                              size="small"
                              onClick={clearSearch}
                              sx={{ minWidth: 'auto', p: 0.5 }}
                            >
                              <Clear fontSize="small" />
                            </Button>
                          </InputAdornment>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  fullWidth
                />
              )}
              noOptionsText={
                searchQuery.length > 0 
                  ? "No caretakers or specialties found matching your search"
                  : "Start typing to search for caretakers or specialties..."
              }
            />
          </Box>

          {/* Search Button */}
          <Button
            variant="contained"
            onClick={onSearch}
            disabled={loading}
            sx={{ minWidth: 120, height: 56 }}
          >
            {loading ? <CircularProgress size={20} /> : 'Search'}
          </Button>
        </Box>

        {/* ZIP / Area filter — FEAT-062 (MVP-18.4): area-match without exposing exact distance */}
        <Box mt={2} maxWidth={220}>
          <TextField
            label="ZIP code"
            placeholder="e.g. 02101"
            value={zipInput}
            onChange={(e) => handleZipChange(e.target.value)}
            error={!!zipError}
            helperText={zipError || 'Filter caregivers by service area'}
            inputProps={{ maxLength: 5, inputMode: 'numeric', 'data-testid': 'search-zip-input' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOn color="action" />
                </InputAdornment>
              ),
              endAdornment: zipInput ? (
                <InputAdornment position="end">
                  <Button
                    size="small"
                    onClick={clearZip}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                    aria-label="Clear ZIP code"
                  >
                    <Clear fontSize="small" />
                  </Button>
                </InputAdornment>
              ) : undefined,
            }}
            size="small"
          />
        </Box>

        {/* Search Tips */}
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            💡 Try searching for: "Physical Therapist", "Nurse", "Occupational Therapy", or a specific caretaker's name
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default CaretakerSearchForm;
